import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SOCIAL_LINKS } from "../data/siteData";
import { useSiteConfig } from "../hooks/useSiteConfig";
import { trackEvent } from "../lib/analytics";

// ─── Inline SVG icons para redes (sin dependencia externa) ───
function IconLinkedIn() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
    </svg>
  );
}
function IconYouTube() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
    </svg>
  );
}
function IconInstagram() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}
function IconX() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
function IconTikTok() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.45a8.16 8.16 0 0 0 4.77 1.52V6.69h-.04z" />
    </svg>
  );
}

const ICON_MAP = {
  linkedin: IconLinkedIn,
  youtube: IconYouTube,
  instagram: IconInstagram,
  x: IconX,
  tiktok: IconTikTok,
};

// Colores reales de cada plataforma — reconocimiento instantáneo de marca
const SOCIAL_COLORS = {
  linkedin: { color: "var(--color-linkedin)", bg: "rgba(10,102,194,0.10)", border: "rgba(10,102,194,0.28)" },
  youtube:  { color: "var(--color-youtube)",  bg: "rgba(255,0,0,0.10)",    border: "rgba(255,0,0,0.28)"   },
  instagram:{ color: "var(--color-instagram-via)", bg: "rgba(225,48,108,0.10)", border: "rgba(225,48,108,0.28)" },
  x:        { color: "var(--color-text-strong)", bg: "rgba(234,234,234,0.05)", border: "rgba(234,234,234,0.18)" },
  tiktok:   { color: "var(--color-tiktok-2)", bg: "rgba(254,44,85,0.10)",  border: "rgba(254,44,85,0.28)" },
};

function NewsletterBanner() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ok | error

  async function onSubmit(e) {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const apiUrl = import.meta.env.VITE_API_URL || "";
      const r = await fetch(`${apiUrl}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setStatus(r.ok ? "ok" : "error");
      if (r.ok) {
        setEmail("");
        trackEvent("newsletter_subscribe", { source: "footer" });
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <div
      style={{
        background: "linear-gradient(135deg, rgba(92,224,190,0.10) 0%, rgba(255,107,74,0.08) 100%)",
        border: "1px solid rgba(92,224,190,0.18)",
        borderRadius: 16,
        padding: "clamp(24px, 4vw, 36px)",
        marginBottom: 48,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Glow decorativo dual */}
      <div aria-hidden="true" style={{
        position: "absolute", top: "-50%", left: "-10%",
        width: 320, height: 320,
        background: "radial-gradient(circle, rgba(92,224,190,0.12) 0%, transparent 60%)",
        borderRadius: "50%",
        pointerEvents: "none",
      }} />
      <div aria-hidden="true" style={{
        position: "absolute", bottom: "-50%", right: "-10%",
        width: 320, height: 320,
        background: "radial-gradient(circle, rgba(255,107,74,0.12) 0%, transparent 60%)",
        borderRadius: "50%",
        pointerEvents: "none",
      }} />
      <div style={{ display: "flex", gap: 28, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span aria-hidden="true" style={{ fontSize: 18 }}>📩</span>
            <span style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--color-accent)" }}>
              Newsletter
            </span>
          </div>
          <h3 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(20px, 2.4vw, 26px)", color: "var(--color-text)", margin: "0 0 6px", lineHeight: 1.2 }}>
            {t("footer.newsletterTitle")}
          </h3>
          <p style={{ fontFamily: "var(--font-body)", fontSize: 13.5, color: "var(--text-muted)", margin: 0, lineHeight: 1.6 }}>
            {t("footer.newsletterDesc")}
          </p>
        </div>

        <form onSubmit={onSubmit} style={{ flex: "1 1 320px", display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (status !== "idle") setStatus("idle"); }}
            placeholder={t("footer.newsletterPlaceholder")}
            aria-label="Email"
            style={{
              flex: "1 1 200px",
              minWidth: 0,
              padding: "12px 16px",
              background: "rgba(8,8,16,0.5)",
              border: "1px solid var(--accent-10)",
              borderRadius: 10,
              color: "var(--color-text)",
              fontFamily: "var(--font-body)",
              fontSize: 14,
              outline: "none",
            }}
            onFocus={(e) => (e.target.style.borderColor = "var(--color-accent)")}
            onBlur={(e) => (e.target.style.borderColor = "var(--accent-10)")}
          />
          <button
            type="submit"
            disabled={status === "loading"}
            style={{
              padding: "12px 24px",
              borderRadius: 10,
              background: "var(--gradient-dual)",
              border: "none",
              fontFamily: "var(--font-body)",
              fontSize: 13.5,
              fontWeight: 700,
              color: "#080810",
              cursor: status === "loading" ? "wait" : "pointer",
              letterSpacing: "0.03em",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(255,107,74,0.25)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
          >
            {status === "loading" ? "..." : t("footer.newsletterCta")}
          </button>
          {status === "ok" && (
            <div style={{ flex: "1 1 100%", fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-accent)" }}>
              ✓ {t("contact.sent")}
            </div>
          )}
          {status === "error" && (
            <div style={{ flex: "1 1 100%", fontFamily: "var(--font-body)", fontSize: 12, color: "#FF6B4A" }}>
              {t("contact.allFieldsRequired")}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

function SocialGrid() {
  const { t } = useTranslation();
  return (
    <div style={{ marginBottom: 48 }}>
      <div style={{ fontFamily: "var(--font-body)", fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-faint)", marginBottom: 16 }}>
        {t("footer.followMe")}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
        {SOCIAL_LINKS.map((s) => {
          const Icon = ICON_MAP[s.id];
          const palette = SOCIAL_COLORS[s.id] || SOCIAL_COLORS.x;
          return (
            <a
              key={s.id}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid rgba(234,234,234,0.05)",
                background: "var(--color-bg-soft)",
                textDecoration: "none",
                transition: "all 0.25s var(--ease-out)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = palette.border;
                e.currentTarget.style.background = palette.bg;
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(234,234,234,0.05)";
                e.currentTarget.style.background = "var(--color-bg-soft)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <span style={{
                color: palette.color,
                display: "flex",
                width: 32,
                height: 32,
                borderRadius: 8,
                alignItems: "center",
                justifyContent: "center",
                background: palette.bg,
                border: `1px solid ${palette.border}`,
                flexShrink: 0,
              }}>
                {Icon ? <Icon /> : null}
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600, color: "var(--color-text-strong)", lineHeight: 1.2 }}>
                  {s.metric}
                  <span style={{ fontWeight: 400, color: "var(--text-faint)", fontSize: 11, marginLeft: 6 }}>{s.label}</span>
                </div>
                <div style={{ fontFamily: "var(--font-body)", fontSize: 10.5, color: "var(--text-ghost)", letterSpacing: "0.04em", marginTop: 2 }}>
                  {s.handle}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function FooterColumns() {
  const { t } = useTranslation();
  const columns = [
    {
      title: t("footer.columns.navigate"),
      links: [
        { to: "/", label: t("nav.home") },
        { to: "/proyectos", label: t("nav.projects") },
        { to: "/sobre-mi", label: t("nav.about") },
        { to: "/asistente", label: t("nav.assistant") },
      ],
    },
    {
      title: t("footer.columns.content"),
      links: [
        { to: "/contenido", label: t("footer.links.allSeries") },
        { to: "/domina-claude", label: t("footer.links.dominaClaude") },
        { to: "/insights", label: t("nav.insights") },
        { to: "/redes", label: t("nav.social") },
      ],
    },
    {
      title: t("footer.columns.work"),
      links: [
        { to: "/servicios", label: t("nav.services") },
        { to: "/agendar", label: t("footer.links.booking30") },
        { to: "/contacto", label: t("nav.contact") },
      ],
    },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 32, marginBottom: 40 }}>
      {columns.map((col) => (
        <div key={col.title}>
          <div style={{ fontFamily: "var(--font-body)", fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-faint)", marginBottom: 14 }}>
            {col.title}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
            {col.links.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-subtle)", textDecoration: "none", transition: "color 0.2s" }}
                  onMouseEnter={(e) => (e.target.style.color = "var(--color-accent)")}
                  onMouseLeave={(e) => (e.target.style.color = "var(--text-subtle)")}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function Footer() {
  const { general } = useSiteConfig();
  const { t } = useTranslation();

  return (
    <footer
      role="contentinfo"
      style={{
        padding: "48px 28px 32px",
        borderTop: "1px solid rgba(92,224,190,0.06)",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <NewsletterBanner />

        {/* Identidad + tagline */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 32 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 8,
              background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-deeper) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-display)",
              fontSize: 15,
              fontWeight: 700,
              color: "#080810",
              flexShrink: 0,
            }}
          >
            {general.initials}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 18, color: "var(--color-text)", lineHeight: 1.2 }}>
              {general.name}
            </div>
            <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-accent)", marginTop: 3, letterSpacing: "0.02em" }}>
              {t("footer.tagline")}
            </div>
          </div>
        </div>

        <SocialGrid />
        <FooterColumns />

        <div
          style={{
            paddingTop: 24,
            borderTop: "1px solid rgba(234,234,234,0.04)",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            fontFamily: "var(--font-body)",
            fontSize: 11,
            color: "var(--text-ghost)",
          }}
        >
          <span>
            © {new Date().getFullYear()} {general.name}. {t("footer.rights")}
          </span>
          <span>
            {t("footer.developedBy")}{" "}
            <a
              href="https://balexus.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--color-accent)", textDecoration: "none", fontWeight: 500 }}
            >
              Balexus.com
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}
